import { Routes } from '@angular/router';
import { LandingPage } from './features/landing-page/landing-page';
import { Login } from './features/login/login';
import { SignupComponent } from './features/signup/signup';
import { Forgotpassword } from './features/forgotpassword/forgotpassword';
import { cumulativePerformances } from './shared/components/cumulative-performances/cumulative-performances';
import { FinalSummaryPerformance } from './shared/components/final-summary-performance/final-summary-performance';
import { AuthCallbackComponent } from './features/auth-callback/auth-callback';
import { VerifyEmailComponent } from './features/verify-email/verify-email';
import { authGuard } from './core/guards/auth-guard';
import { adminGuard } from './core/guards/admin-guard';

export const routes: Routes = [
  { path: 'login', component: Login },
  { path: 'signup', component: SignupComponent },
  { path: 'reset-password', component: Forgotpassword },
  { path: 'auth/callback', component: AuthCallbackComponent },
  { path: 'auth/verify-email', component: VerifyEmailComponent },
  { path: 'auth/v/:short_code', component: VerifyEmailComponent },

  // Admin routes
  {
    path: 'admin/login',
    loadComponent: () =>
      import('./features/admin/admin-login/admin-login').then((m) => m.AdminLogin),
  },
  {
    path: 'admin',
    canActivate: [adminGuard],
    loadComponent: () =>
      import('./features/admin/admin-dashboard/admin-dashboard').then((m) => m.AdminDashboard),
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/admin/admin-dashboard/dashboard-home/dashboard-home').then(
            (m) => m.DashboardHome,
          ),
      },
      {
        path: 'users',
        loadComponent: () => import('./features/admin/users/users').then((m) => m.AdminUsers),
      },
      {
        path: 'users/:userId/broker-accounts',
        loadComponent: () =>
          import('./features/admin/users/user-broker-accounts/user-broker-accounts').then(
            (m) => m.UserBrokerAccounts,
          ),
      },
      {
        path: 'brokers',
        loadComponent: () => import('./features/admin/brokers/brokers').then((m) => m.AdminBrokers),
      },
      {
        path: 'plans',
        loadComponent: () => import('./features/admin/plans/plans').then((m) => m.AdminPlans),
      },
      {
        path: 'support-tickets',
        loadComponent: () =>
          import('./features/admin/support-tickets/support-tickets').then(
            (m) => m.AdminSupportTickets,
          ),
      },
      {
        path: 'logs',
        loadComponent: () =>
          import('./features/admin/application-logs/application-logs').then(
            (m) => m.ApplicationLogs,
          ),
      },
      {
        path: 'celery-tasks',
        loadComponent: () =>
          import('./features/admin/celery-tasks/celery-tasks').then((m) => m.CeleryTasks),
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },

  // Protected routes (require authentication)
  {
    path: '',
    component: LandingPage,
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () => import('./features/dashboard/dashboard').then((m) => m.Dashboard),
      },
      {
        path: 'products',
        loadComponent: () => import('./features/products/products').then((m) => m.Products),
      },
      {
        path: 'broker-settings',
        loadComponent: () =>
          import('./features/broker-settings/broker-settings').then((m) => m.BrokerSettings),
      },
      {
        path: 'add-broker',
        loadComponent: () =>
          import('./features/broker-settings/add-broker/add-broker').then((m) => m.AddBroker),
      },
      {
        path: 'brokers/zerodha/callback',
        loadComponent: () =>
          import('./features/broker-settings/zerodha-callback/zerodha-callback').then(
            (m) => m.ZerodhaCallbackComponent,
          ),
      },
      {
        path: 'brokers/angel/callback',
        loadComponent: () =>
          import('./features/broker-settings/angel-callback/angel-callback').then(
            (m) => m.AngelCallbackComponent,
          ),
      },
      {
        path: 'brokers/dhan/callback',
        loadComponent: () =>
          import('./features/broker-settings/dhan-callback/dhan-callback').then(
            (m) => m.DhanCallbackComponent,
          ),
      },
      {
        path: 'main-notifications',
        loadComponent: () =>
          import('./features/dashboard/main-notification/main-notification').then(
            (m) => m.MainNotification,
          ),
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./shared/components/notifications/notifications-list/notifications-list').then(
            (m) => m.NotificationsListComponent,
          ),
      },
      {
        path: 'performance-stats',
        loadComponent: () =>
          import('./features/performance-stats/performance-stats').then((m) => m.PerformanceStats),
      },
      {
        path: 'settings',
        loadComponent: () => import('./features/settings/settings').then((m) => m.Settings),
      },
      {
        path: 'contact-us',
        loadComponent: () => import('./features/user-help/user-help').then((m) => m.UserHelp),
      },
      {
        path: 'help',
        loadComponent: () => import('./features/help/help').then((m) => m.Help),
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'payment',
        loadComponent: () =>
          import('./features/transactions/payment/payment').then((m) => m.Payment),
        children: [
          { path: '', redirectTo: 'order-summary', pathMatch: 'full' },
          {
            path: 'success',
            loadComponent: () =>
              import('./features/transactions/payment/success/success').then((m) => m.Success),
          },
          {
            path: 'failure',
            loadComponent: () =>
              import('./features/transactions/payment/failure/failure').then((m) => m.Failure),
          },
          {
            path: 'order-summary',
            loadComponent: () =>
              import('./features/transactions/payment/order-summary/order-summary').then(
                (m) => m.OrderSummary,
              ),
          },
        ],
      },
    ],
  },

  // Catch-all route - redirect to dashboard (auth guard will handle authentication)
  { path: '**', redirectTo: 'dashboard' },
];
